// ACTUALIZAR SearchableSelect.js - Búsqueda mejorada para tu API

import React, { useState, useRef, useEffect } from 'react';
import { Search, ChevronDown, ChevronUp, X, MapPin, Calendar, DollarSign } from 'lucide-react';

const SearchableSelect = ({ 
  options = [], 
  onSelect, 
  placeholder = "Buscar...", 
  className = "",
  formatOption = null,
  formatSelected = null,
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Filtrar opciones basado en el término de búsqueda - ESPECÍFICO PARA TU API
  useEffect(() => {
    if (!searchTerm) {
      setFilteredOptions(options);
    } else {
      const filtered = options.filter(option => {
        if (!option) return false;
        
        // Campos específicos de tu API para buscar
        const searchFields = [
          option.destino?.nombre,
          option.hotel,
          option.duracion,
          option.transporte,
          option.regimen,
          option.tarifa?.toString(),
          // También buscar en ciudades
          ...(option.ciudades?.map(c => c.nombre) || [])
        ].filter(Boolean);
        
        return searchFields.some(field => 
          field && field.toString().toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredOptions(filtered);
    }
  }, [searchTerm, options]);

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Formatear opción por defecto - ESPECÍFICO PARA TU API
const defaultFormatOption = (option) => {
  if (!option) return 'Opción no válida';
  
  const destino = option.destino?.nombre || 'Sin destino';
  const duracion = option.duracion || '';
  const hotel = option.hotel || '';
  
  // CORRECCIÓN: Detectar moneda correctamente
  let precioTexto = '';
  const moneda = option.moneda || 'ARS';
  const tarifa = option.tarifa || '';
  
  if (tarifa) {
    if (moneda === 'U$S' || moneda === 'USD' || moneda === 'US$') {
      precioTexto = `U$S ${tarifa}`;
    } else {
      precioTexto = `$${tarifa} ARS`;
    }
  }
  
  let display = destino;
  if (duracion) display += ` (${duracion})`;
  if (precioTexto) display += ` - ${precioTexto}`;
  if (hotel && hotel !== 'Consultar hoteleria') display += ` | ${hotel}`;
  
  return display;
};

  const formatOptionDisplay = formatOption || defaultFormatOption;
  const formatSelectedDisplay = formatSelected || defaultFormatOption;

  const handleSelect = (option) => {
    setSelectedOption(option);
    setSearchTerm('');
    setIsOpen(false);
    if (onSelect) {
      onSelect(option);
    }
  };

  const handleClear = () => {
    setSelectedOption(null);
    setSearchTerm('');
    if (onSelect) {
      onSelect(null);
    }
  };

  const handleInputClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setTimeout(() => inputRef.current?.focus(), 100);
      }
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Input principal */}
      <div 
        className={`w-full px-4 py-3 border rounded-lg cursor-pointer flex items-center justify-between transition-all ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:border-blue-500'
        } ${isOpen ? 'border-blue-500 ring-2 ring-blue-500 ring-opacity-20' : 'border-gray-300'}`}
        onClick={handleInputClick}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-500'}>
          {selectedOption ? formatSelectedDisplay(selectedOption) : placeholder}
        </span>
        
        <div className="flex items-center gap-2">
          {selectedOption && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={disabled}
            >
              <X size={16} />
            </button>
          )}
          {isOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Campo de búsqueda */}
          <div className="p-3 border-b border-gray-200 bg-gray-50">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Buscar por destino, hotel, duración..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-blue-500"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          {/* Lista de opciones */}
          <div className="max-h-72 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                {searchTerm ? 'No se encontraron paquetes' : 'No hay paquetes disponibles'}
                {searchTerm && (
                  <div className="text-sm mt-1">
                    Intenta buscar por: destino, hotel, duración o transporte
                  </div>
                )}
              </div>
            ) : (
              filteredOptions.map((option, index) => {
                const id = option.id || index;

                  // MOSTRAR PRECIO CON MONEDA CORRECTA
                  let precioDisplay = '';
                  const moneda = option.moneda || 'ARS';
                  const tarifa = option.tarifa || '';
                  
                  if (tarifa) {
                    if (moneda === 'U$S' || moneda === 'USD' || moneda === 'US$') {
                      precioDisplay = `U$S ${tarifa}`;
                    } else {
                      precioDisplay = `$${tarifa} ARS`;
                    }
                  }

                return (
                  <div
                    key={id}
                    className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                    onClick={() => handleSelect(option)}
                  >
                    {/* Título principal */}
                    <div className="font-medium text-gray-900 mb-1">
                      {option.destino?.nombre || 'Sin destino'} 
                      {/*{option.duracion && <span className="text-blue-600"> ({option.duracion})</span>}*/}
                    </div>
                    
                    {/* Detalles del paquete */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-600">
                      {option.hotel && (
                        <span className="flex items-center gap-1">
                          🏨 {option.hotel}
                        </span>
                      )}
                      {precioDisplay && (
                        <span className={`flex items-center gap-1 font-semibold ${
                          moneda === 'U$S' || moneda === 'USD' ? 'text-blue-600' : 'text-green-600'
                        }`}>
                          <DollarSign size={12} />
                          {precioDisplay}
                        </span>
                      )}
                      {option.transporte && (
                        <span className="flex items-center gap-1">
                          ✈️ {option.transporte}
                        </span>
                      )}
                      {option.fecha_de_salida && (
                        <span className="flex items-center gap-1">
                          <Calendar size={12} />
                          {new Date(option.fecha_de_salida).toLocaleDateString('es-ES')}
                        </span>
                      )}
                      {option.disponibilidad && (
                        <span className="flex items-center gap-1 text-orange-600">
                          👥 {option.disponibilidad} cupos
                        </span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer con contador */}
          {filteredOptions.length > 0 && (
            <div className="p-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500 text-center">
              Mostrando {filteredOptions.length} de {options.length} paquetes
              {searchTerm && <span className="font-medium"> con "{searchTerm}"</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;